/** API helpers — Job comments, attachments, reviews + Dispatch accept/decline. */
import { api } from "@/lib/api";
import type { Page } from "@/lib/api-cadastros";

// ── Comments ─────────────────────────────────────────────────────────────
export interface JobComment {
  id: string;
  job_id: string;
  author_id: string | null;
  author_name: string | null;
  author_role: string | null;
  body: string;
  is_internal: boolean;
  pinned: boolean;
  created_at: string;
  updated_at: string;
}

export const jobCommentsApi = {
  list: async (jobId: string): Promise<Page<JobComment>> => {
    const { data } = await api.get<Page<JobComment>>(`/v1/jobs/${jobId}/comments`, { params: { size: 100 } });
    return data;
  },
  create: async (jobId: string, body: { body: string; is_internal?: boolean; pinned?: boolean }): Promise<JobComment> => {
    const { data } = await api.post<JobComment>(`/v1/jobs/${jobId}/comments`, body);
    return data;
  },
  update: async (jobId: string, id: string, body: Partial<JobComment>): Promise<JobComment> => {
    const { data } = await api.patch<JobComment>(`/v1/jobs/${jobId}/comments/${id}`, body);
    return data;
  },
  remove: async (jobId: string, id: string): Promise<void> => {
    await api.delete(`/v1/jobs/${jobId}/comments/${id}`);
  },
};

// ── Attachments ───────────────────────────────────────────────────────────
export interface JobAttachment {
  id: string;
  job_id: string;
  filename: string;
  content_type: string;
  size_bytes: number;
  storage_path: string;
  uploaded_by_id: string | null;
  created_at: string;
}

export const jobAttachmentsApi = {
  list: async (jobId: string): Promise<Page<JobAttachment>> => {
    const { data } = await api.get<Page<JobAttachment>>(`/v1/jobs/${jobId}/attachments`, { params: { size: 100 } });
    return data;
  },
  getUploadUrl: async (jobId: string, body: { filename: string; content_type: string; size_bytes: number }) => {
    const { data } = await api.post<{ upload_url: string; storage_path: string; expires_at: string }>(
      `/v1/jobs/${jobId}/attachments/upload-url`, body,
    );
    return data;
  },
  register: async (jobId: string, body: { filename: string; content_type: string; size_bytes: number; storage_path: string }): Promise<JobAttachment> => {
    const { data } = await api.post<JobAttachment>(`/v1/jobs/${jobId}/attachments`, body);
    return data;
  },
  remove: async (jobId: string, id: string): Promise<void> => {
    await api.delete(`/v1/jobs/${jobId}/attachments/${id}`);
  },
};

// ── Reviews ───────────────────────────────────────────────────────────────
export type ReviewDirection = "client_to_nerd" | "nerd_to_client";

export interface JobReview {
  id: string;
  job_id: string;
  reviewer_id: string | null;
  direction: ReviewDirection;
  score: number;
  comment: string | null;
  submitted_at: string;
}

export const jobReviewsApi = {
  list: async (jobId: string): Promise<JobReview[]> => {
    const { data } = await api.get<JobReview[]>(`/v1/jobs/${jobId}/reviews`);
    return data;
  },
  submit: async (jobId: string, body: { direction: ReviewDirection; score: number; comment?: string }): Promise<JobReview> => {
    const { data } = await api.post<JobReview>(`/v1/jobs/${jobId}/reviews`, body);
    return data;
  },
};

// ── Dispatch actions ──────────────────────────────────────────────────────
export const dispatchActionsApi = {
  accept: async (shortlistId: string) => {
    const { data } = await api.post(`/v1/dispatch/shortlist/${shortlistId}/accept`);
    return data;
  },
  decline: async (shortlistId: string) => {
    const { data } = await api.post(`/v1/dispatch/shortlist/${shortlistId}/decline`);
    return data;
  },
};
